/**
 * Compact financial snapshot for a project - a typographic strip, not four giant cards.
 * Draft entries are called out separately so they can never be mistaken for actuals.
 */
import React from 'react';
import { Info } from '@phosphor-icons/react';
import { cn } from '../../../lib/utils';
import { money, percent, usedPercent, varianceOf, varianceClass, varianceLabel } from './financeFormat';

const Metric = ({ label, value, sub, subClass, testId }) => (
  <div className="px-5 py-4 flex-1 min-w-[150px]" data-testid={testId}>
    <p className="text-[10px] font-medium uppercase tracking-[0.12em] text-foreground-muted">{label}</p>
    <p className="text-xl font-semibold tabular-nums mt-1">{value}</p>
    {sub && <p className={cn('text-xs mt-0.5', subClass || 'text-foreground-muted')}>{sub}</p>}
  </div>
);

export const FinancialsSummaryStrip = ({ report, pending, currency = 'EUR' }) => {
  // Costs are the project's budget in the everyday sense; revenue is reported separately
  const costs = (report?.by_phase || []).reduce(
    (acc, phase) => ({
      budget: acc.budget + Number(phase.costs?.budget || 0),
      actual: acc.actual + Number(phase.costs?.actual || 0),
    }),
    { budget: 0, actual: 0 }
  );
  const income = (report?.by_phase || []).reduce(
    (acc, phase) => ({
      budget: acc.budget + Number(phase.income?.budget || 0),
      actual: acc.actual + Number(phase.income?.actual || 0),
    }),
    { budget: 0, actual: 0 }
  );

  const variance = varianceOf(costs.budget, costs.actual);
  const used = usedPercent(costs.actual, costs.budget);
  const draftAmount = Number(pending?.draft_amount || 0);

  return (
    <div className="bg-white rounded-xl border border-black/[0.04]" data-testid="financials-summary-strip">
      <div className="flex flex-wrap divide-x divide-black/[0.05]">
        <Metric
          label="Current budget"
          value={money(costs.budget, { currency })}
          sub="Approved cost plan"
          testId="summary-current-budget"
        />
        <Metric
          label="Actual to date"
          value={money(costs.actual, { currency })}
          sub={`${percent(used)} of budget`}
          testId="summary-actual"
        />
        <Metric
          label="Variance"
          value={money(Math.abs(variance.diff), { currency })}
          sub={varianceLabel(variance, currency)}
          subClass={varianceClass(variance)}
          testId="summary-variance"
        />
        <Metric
          label="Budget used"
          value={percent(used)}
          sub={`${money(variance.remaining, { currency })} remaining`}
          testId="summary-used"
        />
        {(income.budget > 0 || income.actual > 0) && (
          <Metric
            label="Income to date"
            value={money(income.actual, { currency })}
            sub={`${money(income.budget, { currency })} planned`}
            testId="summary-income"
          />
        )}
      </div>

      {draftAmount > 0 && (
        <div
          className="flex items-center gap-2 px-5 py-2.5 border-t border-black/[0.05] text-xs text-amber-800 bg-amber-50/60 rounded-b-xl"
          data-testid="summary-draft-note"
        >
          <Info className="h-3.5 w-3.5 flex-shrink-0" weight="duotone" />
          <span>
            <strong className="font-semibold">{money(draftAmount, { currency })}</strong> in draft
            entries is <strong className="font-semibold">not</strong> included above. Potential
            actual after approval: {money(costs.actual + draftAmount, { currency })}.
          </span>
        </div>
      )}
    </div>
  );
};

export default FinancialsSummaryStrip;
